"use client";
import { useProgressRouter } from "@lib/util/navigation";
import { clx } from "@medusajs/ui";
import { ChevronLeft, ChevronRight } from "lucide-react"; // Import icon
import { usePathname, useSearchParams } from "next/navigation";
import { useState } from "react";

export function Pagination({
  page,
  totalPages,
  "data-testid": dataTestid,
}: {
  page: number;
  totalPages: number;
  "data-testid"?: string;
}) {
  const router = useProgressRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || isNavigating) return;
    setIsNavigating(true);

    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    router.push(`${pathname}?${params.toString()}`, {
      scroll: false,
    });

    setTimeout(() => {
      setIsNavigating(false);
    }, 500);
  };

  const renderPageButton = (p: number, label: string | number, isCurrent: boolean) => (
    <button
      key={p}
      className={clx(
        "txt-xlarge-plus text-ui-fg-muted rounded-lg border py-1 px-3 hover:text-primary hover:border-primary",
        {
          "text-primary border-primary hover:text-ui-fg-subtle": isCurrent,
        }
      )}
      disabled={isCurrent}
      onClick={() => handlePageChange(p)}
    >
      {label}
    </button>
  );

  const renderEllipsis = (key: string) => (
    <span key={key} className="txt-xlarge-plus text-ui-fg-muted cursor-default">
      ...
    </span>
  );

  const renderPageButtons = () => {
    const buttons = [];

    if (totalPages <= 7) {
      buttons.push(...Array.from({ length: totalPages }, (_, i) => renderPageButton(i + 1, i + 1, i + 1 === page)));
    } else {
      if (page <= 4) {
        buttons.push(...Array.from({ length: 5 }, (_, i) => renderPageButton(i + 1, i + 1, i + 1 === page)));
        buttons.push(renderEllipsis("ellipsis1"));
        buttons.push(renderPageButton(totalPages, totalPages, totalPages === page));
      } else if (page >= totalPages - 3) {
        buttons.push(renderPageButton(1, 1, 1 === page));
        buttons.push(renderEllipsis("ellipsis2"));
        buttons.push(
          ...Array.from({ length: 5 }, (_, i) =>
            renderPageButton(totalPages - 4 + i, totalPages - 4 + i, totalPages - 4 + i === page)
          )
        );
      } else {
        buttons.push(renderPageButton(1, 1, 1 === page));
        buttons.push(renderEllipsis("ellipsis3"));
        buttons.push(renderPageButton(page - 1, page - 1, false));
        buttons.push(renderPageButton(page, page, true));
        buttons.push(renderPageButton(page + 1, page + 1, false));
        buttons.push(renderEllipsis("ellipsis4"));
        buttons.push(renderPageButton(totalPages, totalPages, totalPages === page));
      }
    }

    return buttons;
  };

  return (
    <div className="flex justify-center w-full mt-6">
      <div className="flex gap-3 items-center" data-testid={dataTestid}>
        {/* Nút Previous */}
        <button
          className="rounded-lg border p-2 hover:text-primary hover:border-primary disabled:opacity-50"
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft size={18} />
        </button>

        {renderPageButtons()}

        {/* Nút Next */}
        <button
          className="rounded-lg border p-2 hover:text-primary hover:border-primary disabled:opacity-50"
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
